/**
 * The line forms a PT field ticket is edited with (PT-5, fieldiq-api#932) —
 * shared by the office ticket page (FieldTicketDetailScreen) and the technician's phone
 * review (field/PtFieldTicketScreen), so the two cannot disagree about what a
 * line needs.
 *
 * PRICES ARE THE OFFICE'S. `canPrice` (PT_DISPATCH) shows the rate controls:
 * the rate card (send no rate / `rate: null`), a set rate, or "No charge" (an
 * explicit 0, which is a price). A technician sends quantities only; the API
 * refuses a rate from anyone else (403 `rate_office_only`), so the control is
 * left off rather than offered and refused.
 *
 * WHAT A LINE COSTS IS NEVER WORKED OUT HERE. The form sends a quantity and,
 * for the office, a rate; the API answers the whole ticket with the line's
 * amount and the subtotal, and that answer is what the screen shows next.
 *
 * A consumable is picked from GET /consumables — the names on the rate card for
 * this customer on the ticket's day, with their units. A name not on it is
 * refused (`consumable_not_found`); the picker offers only listed ones. An
 * equipment line names exactly one of an instrument kind or a specific PT
 * instrument, as the API requires.
 */
import React, { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAddPtLine, usePtConsumables, usePtInstruments, useRemovePtLine, useUpdatePtLine } from '../../hooks/usePressureTesting';
import { queryLoadFailure } from '../../lib/load-failure';
import { PT_LINE_KIND_LABEL, ptKindLabel, ptRateText } from '../../lib/pressure-testing';
import { PT_INSTRUMENT_KINDS, type PtLineCreate, type PtLineKind, type PtTicketDetail, type PtTicketLine } from '../../types/pressure-testing';
import { PT_INPUT, PT_SECONDARY, PtField } from './PtBits';
import { FormError, ptWriteError } from './PtJobDialogs';

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-60';

/** A quantity or rate as the API takes it: ≥ 0, at most 2 decimals. Null when blank, NaN when not that. */
export function ptAmountFromInput(text: string): number | null {
  const t = text.trim();
  if (t === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return NaN;
  return Number(t);
}

/** The kinds a technician adds on the phone ("Add charge"). */
export const PT_FIELD_CHARGE_KINDS = ['consumable', 'standby_hour', 'mileage_km', 'equipment_day', 'equipment_hour'] as const;
/** Every kind a person may add by hand. A test line only comes from a recorded test. */
export const PT_OFFICE_LINE_KINDS = [
  'labour_hour', 'consumable', 'standby_hour', 'mileage_km', 'equipment_day', 'equipment_hour', 'mobilization', 'other',
] as const;
type ManualKind = Exclude<PtLineKind, 'test'>;

/** The unit a kind is counted in, as the API's KIND_UNIT says; a consumable's comes from its rate row. */
const KIND_UNIT: Record<Exclude<ManualKind, 'consumable'>, string> = {
  labour_hour: 'hour', equipment_day: 'day', equipment_hour: 'hour', mobilization: 'each', mileage_km: 'km', standby_hour: 'hour', other: 'each',
};

type RateMode = 'card' | 'set' | 'zero';

function RateControl({
  mode,
  setMode,
  value,
  setValue,
  big,
  cardLabel,
}: {
  mode: RateMode;
  setMode: (m: RateMode) => void;
  value: string;
  setValue: (v: string) => void;
  big?: boolean;
  cardLabel: string;
}) {
  const opt = (m: RateMode, label: string) => (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-fieldiq-text">
      <input type="radio" name="pt-line-rate-mode" checked={mode === m} onChange={() => setMode(m)} className="size-4 accent-fieldiq-accent" data-testid={`pt-line-rate-${m}`} />
      {label}
    </label>
  );
  return (
    <fieldset className="space-y-2">
      <legend className="mb-1 text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Rate (office only)</legend>
      {opt('card', cardLabel)}
      {opt('set', 'Set the rate')}
      {mode === 'set' && (
        <input
          className={`${PT_INPUT} ${big ? 'h-12 text-base' : ''}`}
          inputMode="decimal"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="0.00"
          aria-label="Rate"
          data-testid="pt-line-rate-value"
        />
      )}
      {opt('zero', 'No charge ($0)')}
    </fieldset>
  );
}

function rateFrom(mode: RateMode, value: string): { rate?: number | null; error?: string } {
  if (mode === 'zero') return { rate: 0 };
  if (mode === 'card') return {};
  const r = ptAmountFromInput(value);
  if (r === null || Number.isNaN(r)) return { error: 'Enter the rate as a number of 0 or more, with at most 2 decimals.' };
  return { rate: r };
}

/**
 * Add a line. `kinds` is what this caller may add; `canPrice` adds the
 * office's rate control. `day` is the day the consumables list is read for —
 * the ticket's work day, else the tenant's today.
 */
export function PtAddLineForm({
  ticket,
  kinds,
  canPrice,
  day,
  big = false,
  onDone,
  onCancel,
}: {
  ticket: PtTicketDetail;
  kinds: readonly ManualKind[];
  canPrice: boolean;
  day: string | null;
  big?: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const add = useAddPtLine();
  const [kind, setKind] = useState<ManualKind>(kinds[0]);
  const [quantity, setQuantity] = useState('');
  const [description, setDescription] = useState('');
  const [consumable, setConsumable] = useState('');
  const [equipment, setEquipment] = useState('');
  const [unit, setUnit] = useState('');
  const [rateMode, setRateMode] = useState<RateMode>(kind === 'other' ? 'set' : 'card');
  const [rateValue, setRateValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isEq = kind === 'equipment_day' || kind === 'equipment_hour';
  const consumables = usePtConsumables({ customer_id: ticket.customer_id, ...(day ? { on: day } : {}) }, kind === 'consumable');
  const instruments = usePtInstruments(undefined);
  const chosen = useMemo(
    () => (consumables.data?.data ?? []).find((c) => c.name === consumable) ?? null,
    [consumables.data, consumable],
  );
  const shownUnit = kind === 'consumable' ? chosen?.unit ?? null : kind === 'other' ? unit.trim() || 'each' : KIND_UNIT[kind];

  const input = `${PT_INPUT} ${big ? 'h-12 text-base' : ''}`;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const q = ptAmountFromInput(quantity);
    if (q === null || Number.isNaN(q)) return setError('Enter the quantity as a number of 0 or more, with at most 2 decimals.');
    if (kind === 'consumable' && !consumable) return setError('Choose a consumable.');
    if (isEq && !equipment) return setError('Choose the equipment.');
    if (kind === 'other' && !description.trim()) return setError('An other charge needs a description.');
    const body: PtLineCreate = { kind, quantity: q };
    if (description.trim()) body.description = description.trim();
    if (kind === 'consumable') body.consumable_name = consumable;
    if (isEq) {
      if (equipment.startsWith('kind:')) body.instrument_kind = equipment.slice(5);
      else body.equipment_id = equipment.slice(3);
    }
    if (kind === 'other' && unit.trim()) body.unit = unit.trim();
    if (canPrice) {
      const r = rateFrom(rateMode, rateValue);
      if (r.error) return setError(r.error);
      if (r.rate !== undefined) body.rate = r.rate;
    }
    add.mutate(
      { ticketId: ticket.id, body },
      {
        onSuccess: () => {
          toast.success(`${PT_LINE_KIND_LABEL[kind]} added to ${ticket.ticket_number}`);
          onDone();
        },
        onError: (err) => setError(ptWriteError(err, 'The charge was not added.').message),
      },
    );
  };

  const consumableList = consumables.data?.data ?? [];
  const instrumentList = (instruments.data?.data ?? []).filter((i) => i.status !== 'retired');

  return (
    <form onSubmit={submit} className="space-y-3" noValidate data-testid="pt-add-line-form">
      <FormError message={error} />
      <PtField label="Charge">
        <select
          className={input}
          value={kind}
          onChange={(e) => {
            const k = e.target.value as ManualKind;
            setKind(k);
            setConsumable('');
            setEquipment('');
            if (k === 'other' && rateMode === 'card') setRateMode('set');
          }}
          data-testid="pt-line-kind"
        >
          {kinds.map((k) => (
            <option key={k} value={k}>
              {PT_LINE_KIND_LABEL[k]}
            </option>
          ))}
        </select>
      </PtField>

      {kind === 'consumable' && (
        <PtField
          label="Consumable"
          hint={
            queryLoadFailure(consumables)
              ? 'The consumables on the rate card could not be loaded.'
              : consumables.isSuccess && !consumableList.length
                ? "No consumables are on the rate card for this customer. The office adds them in Settings → Pressure Testing → Rate card."
                : undefined
          }
        >
          <select className={input} value={consumable} onChange={(e) => setConsumable(e.target.value)} data-testid="pt-line-consumable">
            <option value="">{consumables.isLoading ? 'Loading…' : 'Choose a consumable'}</option>
            {consumableList.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
                {c.unit ? ` (${c.unit})` : ''}
              </option>
            ))}
          </select>
        </PtField>
      )}

      {isEq && (
        <PtField label="Equipment" hint="A kind of instrument, or one instrument by name.">
          <select className={input} value={equipment} onChange={(e) => setEquipment(e.target.value)} data-testid="pt-line-equipment">
            <option value="">Choose the equipment</option>
            <optgroup label="By kind">
              {PT_INSTRUMENT_KINDS.map((k) => (
                <option key={k} value={`kind:${k}`}>
                  {ptKindLabel(k)}
                </option>
              ))}
            </optgroup>
            {instrumentList.length > 0 && (
              <optgroup label="A specific instrument">
                {instrumentList.map((i) => (
                  <option key={i.id} value={`eq:${i.id}`}>
                    {i.name}
                    {i.serial_number ? ` · ${i.serial_number}` : ''}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </PtField>
      )}

      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2">
        <PtField label="Quantity">
          <input
            className={input}
            inputMode="decimal"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="0"
            data-testid="pt-line-quantity"
            autoFocus={!big}
          />
        </PtField>
        <span className="pb-2.5 text-sm font-semibold text-fieldiq-text" data-testid="pt-line-unit">
          {shownUnit ?? '—'}
        </span>
      </div>

      {kind === 'other' && (
        <PtField label="Unit" hint="Defaults to each.">
          <input className={input} value={unit} onChange={(e) => setUnit(e.target.value)} maxLength={20} />
        </PtField>
      )}

      <PtField label={kind === 'other' ? 'Description' : 'Description (optional)'}>
        <input className={input} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={300} data-testid="pt-line-description" />
      </PtField>

      {canPrice && (
        <RateControl
          mode={rateMode}
          setMode={setRateMode}
          value={rateValue}
          setValue={setRateValue}
          big={big}
          cardLabel={kind === 'other' ? 'Leave unpriced' : 'From the rate card'}
        />
      )}

      <div className={big ? 'grid grid-cols-2 gap-2' : 'flex justify-end gap-2'}>
        <button type="button" className={big ? `${PT_SECONDARY} h-12 justify-center` : PT_SECONDARY} onClick={onCancel} disabled={add.isPending}>
          Cancel
        </button>
        <button type="submit" className={big ? `${PRIMARY} h-12 justify-center` : PRIMARY} disabled={add.isPending} data-testid="pt-line-add-submit">
          {add.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          Add
        </button>
      </div>
    </form>
  );
}

/**
 * Edit a line: its quantity (anyone allowed on the ticket), its description (a
 * manual line only — an automatic one keeps the API's), and for the office its
 * rate. Only the fields that changed are sent.
 */
export function PtEditLineForm({
  line,
  currency,
  canPrice,
  big = false,
  onDone,
  onCancel,
}: {
  line: PtTicketLine;
  currency: string | null;
  canPrice: boolean;
  big?: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const update = useUpdatePtLine();
  const [quantity, setQuantity] = useState(line.quantity === null ? '' : String(line.quantity));
  const [description, setDescription] = useState(line.description);
  const startMode: RateMode = line.rate_source === 'office' ? (line.rate === 0 ? 'zero' : 'set') : 'card';
  const [rateMode, setRateMode] = useState<RateMode>(startMode);
  const [rateValue, setRateValue] = useState(line.rate_source === 'office' && line.rate !== null ? String(line.rate) : '');
  const [error, setError] = useState<string | null>(null);
  const input = `${PT_INPUT} ${big ? 'h-12 text-base' : ''}`;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const body: { quantity?: number; description?: string; rate?: number | null } = {};
    const q = ptAmountFromInput(quantity);
    if (q === null || Number.isNaN(q)) return setError('Enter the quantity as a number of 0 or more, with at most 2 decimals.');
    if (q !== line.quantity) body.quantity = q;
    if (line.source !== 'auto' && description.trim() !== line.description) {
      if (!description.trim()) return setError('The description cannot be empty.');
      body.description = description.trim();
    }
    if (canPrice) {
      if (rateMode === 'card') {
        // Back to the rate card only when it is on an office price now.
        if (line.rate_source === 'office') body.rate = null;
      } else {
        const r = rateFrom(rateMode, rateValue);
        if (r.error) return setError(r.error);
        if (!(line.rate_source === 'office' && line.rate === r.rate)) body.rate = r.rate as number;
      }
    }
    if (Object.keys(body).length === 0) return onCancel();
    update.mutate(
      { lineId: line.id, body },
      {
        onSuccess: () => {
          toast.success('Line updated');
          onDone();
        },
        onError: (err) => setError(ptWriteError(err, 'The line was not changed.').message),
      },
    );
  };

  return (
    <form onSubmit={submit} className="space-y-3" noValidate data-testid="pt-edit-line-form">
      <FormError message={error} />
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2">
        <PtField label="Quantity">
          <input
            className={input}
            inputMode="decimal"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            data-testid="pt-edit-line-quantity"
            autoFocus
          />
        </PtField>
        <span className="pb-2.5 text-sm font-semibold text-fieldiq-text">{line.unit || '—'}</span>
      </div>
      {line.source === 'auto' ? (
        <p className="text-xs text-fieldiq-muted">An automatic line keeps its description.</p>
      ) : (
        <PtField label="Description">
          <input className={input} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={300} />
        </PtField>
      )}
      {canPrice && (
        <>
          <RateControl
            mode={rateMode}
            setMode={setRateMode}
            value={rateValue}
            setValue={setRateValue}
            big={big}
            cardLabel={line.kind === 'other' ? 'Leave unpriced' : 'From the rate card'}
          />
          <p className="text-xs text-fieldiq-muted">
            Now: {ptRateText(line.rate, currency, line.unit)}
            {line.rate_source === 'office' ? ' (set by the office)' : line.rate_source === 'rate_card' ? ' (rate card)' : ''}
          </p>
        </>
      )}
      <div className={big ? 'grid grid-cols-2 gap-2' : 'flex justify-end gap-2'}>
        <button type="button" className={big ? `${PT_SECONDARY} h-12 justify-center` : PT_SECONDARY} onClick={onCancel} disabled={update.isPending}>
          Cancel
        </button>
        <button type="submit" className={big ? `${PRIMARY} h-12 justify-center` : PRIMARY} disabled={update.isPending} data-testid="pt-edit-line-save">
          {update.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          Save
        </button>
      </div>
    </form>
  );
}

/** Remove a line, with a reason. The API keeps it as removed and audits it. */
export function PtRemoveLineForm({ line, big = false, onDone, onCancel }: { line: PtTicketLine; big?: boolean; onDone: () => void; onCancel: () => void }) {
  const remove = useRemovePtLine();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!reason.trim()) return setError('A reason is required to remove a line.');
    remove.mutate(
      { lineId: line.id, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success('Line removed');
          onDone();
        },
        onError: (err) => setError(ptWriteError(err, 'The line was not removed.').message),
      },
    );
  };

  return (
    <form onSubmit={submit} className="space-y-3" noValidate data-testid="pt-remove-line-form">
      <p className="text-sm text-fieldiq-text">
        Remove <span className="font-bold">{line.description}</span>?{' '}
        {line.kind === 'test' ? 'A removed test line is not billed on a later ticket either.' : ''}
      </p>
      <FormError message={error} />
      <PtField label="Reason">
        <textarea
          className={`${PT_INPUT} h-20 py-2 ${big ? 'text-base' : ''}`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          data-testid="pt-remove-line-reason"
          autoFocus
        />
      </PtField>
      <div className={big ? 'grid grid-cols-2 gap-2' : 'flex justify-end gap-2'}>
        <button type="button" className={big ? `${PT_SECONDARY} h-12 justify-center` : PT_SECONDARY} onClick={onCancel} disabled={remove.isPending}>
          Keep it
        </button>
        <button
          type="submit"
          disabled={remove.isPending}
          data-testid="pt-remove-line-confirm"
          className={`inline-flex items-center justify-center gap-1.5 rounded-fi-control border border-black/20 bg-fi-signal-red px-3 text-sm font-bold text-fi-signal-ink disabled:cursor-not-allowed disabled:opacity-60 ${big ? 'h-12' : 'h-9'}`}
        >
          {remove.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          Remove line
        </button>
      </div>
    </form>
  );
}
